"use client";

import React, { useEffect, useState } from "react";
import { fetchWithAuth } from "@/lib/auth";

// ─── Types ────────────────────────────────────────────────────────────────────

type Side = Record<string, string | null>;  // field key → value (null = absent)

type SyncStatus = "match" | "drift" | "only_wp" | "only_db";

interface GroupDiff {
    name: string;
    type: string;
    raw_wp_type: string;
    sync_status: SyncStatus;
    drifted_fields: string[];
    wp: Side | null;
    db: Side | null;
}

// Ordered field definitions mirroring backend COMPARABLE_FIELDS
// Only group-specific content fields — boilerplate governance templates excluded
const FIELD_DEFS: { key: string; label: string }[] = [
    { key: "status",                        label: "Status" },
    { key: "about",                         label: "About" },
    { key: "mission_statement",             label: "Mission Statement" },
    { key: "in_scope",                      label: "In Scope" },
    { key: "out_of_scope",                  label: "Out of Scope" },
    { key: "core_responsibilities",         label: "Core Responsibilities" },
    { key: "core_activities",               label: "Core Activities" },
    { key: "key_competencies",              label: "Key Competencies" },
    { key: "key_deliverables",              label: "Key Deliverables" },
    { key: "meeting_frequency",             label: "Meeting Frequency" },
    { key: "recommended_time_commitment",   label: "Recommended Time Commitment" },
    { key: "suggested_career_age_ta_depth", label: "Suggested Career / TA Depth" },
    { key: "eligible_cmt_journey_stages",   label: "Eligible CMT Journey Stages" },
    { key: "updates_recent_news",           label: "Recent News" },
    { key: "updates_upcoming_activities",   label: "Upcoming Activities" },
    { key: "region_code",                   label: "Region Code" },
    { key: "chapter_code",                  label: "Chapter Code" },
    { key: "date_adopted_last_amended",     label: "Date Adopted / Last Amended" },
];

interface ScanSummary {
    total_wp: number;
    total_db: number;
    matched: number;
    drift: number;
    only_wp: number;
    only_db: number;
    in_sync: boolean;
}

interface ScanResult {
    scanned_at: string;
    summary: ScanSummary;
    groups: GroupDiff[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<SyncStatus, { label: string; pill: string; dot: string; rowBg: string; borderColor: string }> = {
    match:   { label: "Match",       pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", rowBg: "",                borderColor: "border-emerald-200" },
    drift:   { label: "Field Drift", pill: "bg-amber-100 text-amber-700",    dot: "bg-amber-400",   rowBg: "bg-amber-50/60",  borderColor: "border-amber-200"   },
    only_wp: { label: "WP Only",     pill: "bg-blue-100 text-blue-700",      dot: "bg-blue-500",    rowBg: "bg-blue-50/60",   borderColor: "border-blue-200"    },
    only_db: { label: "DB Only",     pill: "bg-rose-100 text-rose-700",      dot: "bg-rose-500",    rowBg: "bg-rose-50/60",   borderColor: "border-rose-200"    },
};

const TYPE_STYLE: Record<string, string> = {
    chapter:   "bg-[#007a8a]/10 text-[#007a8a]",
    community: "bg-[#ae232d]/10 text-[#ae232d]",
    committee: "bg-[#003d45]/10 text-[#003d45]",
};

const SYNC_FILTER_OPTIONS: { label: string; value: SyncStatus }[] = [
    { label: "Match",    value: "match"   },
    { label: "Drift",    value: "drift"   },
    { label: "WP Only",  value: "only_wp" },
    { label: "DB Only",  value: "only_db" },
];

const TYPE_FILTER_OPTIONS: { label: string; value: string }[] = [
    { label: "Chapter",   value: "chapter"   },
    { label: "Community", value: "community" },
    { label: "Committee", value: "committee" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function typeLabel(group: GroupDiff) {
    if (group.raw_wp_type) return group.raw_wp_type.replace(/_/g, " ");
    return group.type || "—";
}

// ─── Expanded detail row (table drawer) ───────────────────────────────────────

function DetailDrawer({ group }: { group: GroupDiff }) {
    const isOnlyWP = group.sync_status === "only_wp";
    const isOnlyDB = group.sync_status === "only_db";
    const [showAll, setShowAll] = useState(false);

    // Fields to show: always show drifted + status; others only if they have a value
    const visibleFields = FIELD_DEFS.filter(({ key }) => {
        const isDrifted = group.drifted_fields.includes(key);
        const wpVal = group.wp?.[key];
        const dbVal = group.db?.[key];
        const hasValue = (wpVal && wpVal !== "TBD") || (dbVal && dbVal !== "TBD");
        return isDrifted || key === "status" || (showAll && hasValue);
    });

    const totalWithValue = FIELD_DEFS.filter(({ key }) => {
        const wpVal = group.wp?.[key];
        const dbVal = group.db?.[key];
        return (wpVal && wpVal !== "TBD") || (dbVal && dbVal !== "TBD");
    }).length;

    return (
        <tr>
            <td colSpan={8} className="px-0 py-0">
                <div className="mx-4 mb-4 mt-0.5 rounded-xl border border-[#e2e8f0] overflow-hidden bg-white shadow-sm">
                    {/* Only-side notice */}
                    {isOnlyWP && (
                        <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 text-[12px] text-blue-700 flex items-center gap-2">
                            <span className="material-symbols-outlined text-sm">info</span>
                            This group exists on WordPress but has no matching record in our VolunteerHub database.
                        </div>
                    )}
                    {isOnlyDB && (
                        <div className="px-4 py-2.5 bg-rose-50 border-b border-rose-100 text-[12px] text-rose-700 flex items-center gap-2">
                            <span className="material-symbols-outlined text-sm">info</span>
                            This group is in our VolunteerHub database but has not been published on WordPress yet.
                        </div>
                    )}

                    {/* Side-by-side comparison table */}
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                <th className="w-44 px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Field</th>
                                <th className="px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-[#007a8a]">
                                    WordPress (cmtassociation.org)
                                </th>
                                <th className="px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-[#003d45]">
                                    VolunteerHub DB
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#f1f5f9]">
                            {visibleFields.map(({ key, label }) => {
                                const isDrifted = group.drifted_fields.includes(key);
                                const wpVal = group.wp?.[key] || null;
                                const dbVal = group.db?.[key] || null;
                                const isStatus = key === "status";

                                return (
                                    <tr key={key} className={isDrifted ? "bg-amber-50" : ""}>
                                        <td className="px-4 py-2.5 font-semibold text-[#64748b] align-top whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                {label}
                                                {isDrifted && (
                                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-[#334155] leading-relaxed align-top">
                                            {wpVal ? (
                                                <div className="flex items-start gap-2">
                                                    <span className="whitespace-pre-wrap">{wpVal}</span>
                                                    {isStatus && group.wp?.slug && (
                                                        <a href={`https://cmtassociation.org/community/${group.wp.slug}/`}
                                                            target="_blank" rel="noreferrer"
                                                            className="text-[10px] text-[#007a8a] underline flex-shrink-0 mt-0.5">
                                                            view ↗
                                                        </a>
                                                    )}
                                                </div>
                                            ) : <span className="italic text-[#cbd5e1]">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-[#334155] leading-relaxed align-top whitespace-pre-wrap">
                                            {dbVal || <span className="italic text-[#cbd5e1]">—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Show all / collapse toggle */}
                    <div className="px-4 py-2 border-t border-[#f1f5f9] flex items-center gap-2">
                        <button
                            onClick={() => setShowAll(v => !v)}
                            className="text-[11px] text-[#007a8a] font-semibold hover:underline"
                        >
                            {showAll
                                ? "Show drifted fields only"
                                : `Show all ${totalWithValue} fields`}
                        </button>
                    </div>
                </div>
            </td>
        </tr>
    );
}

// ─── Main table ───────────────────────────────────────────────────────────────

function SyncTable({ groups }: { groups: GroupDiff[] }) {
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    function toggle(key: string) {
        setExpandedRow(prev => prev === key ? null : key);
    }

    return (
        <div className="rounded-xl border border-[#e2e8f0] overflow-hidden bg-white">
            <table className="w-full text-[13px]">
                <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8] w-6"></th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Group Name</th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Type</th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">WP Status</th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">DB Status</th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Sync</th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Fields</th>
                        <th className="w-8 px-2"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                    {groups.length === 0 ? (
                        <tr>
                            <td colSpan={8} className="px-4 py-10 text-center text-[#94a3b8] text-[13px]">
                                No groups match the current filter.
                            </td>
                        </tr>
                    ) : groups.map(g => {
                        const key = `${g.name}__${g.sync_status}`;
                        const cfg = STATUS_CONFIG[g.sync_status];
                        const isOpen = expandedRow === key;

                        return (
                            <React.Fragment key={key}>
                                <tr
                                    onClick={() => toggle(key)}
                                    className={`cursor-pointer transition-colors hover:bg-[#f8fafc] ${cfg.rowBg}`}
                                >
                                    {/* Dot */}
                                    <td className="px-4 py-3">
                                        <span className={`block w-2 h-2 rounded-full ${cfg.dot}`} />
                                    </td>

                                    {/* Name */}
                                    <td className="px-4 py-3 font-semibold text-[#1e293b]">{g.name}</td>

                                    {/* Type */}
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${TYPE_STYLE[g.type] || "bg-slate-100 text-slate-600"}`}>
                                            {typeLabel(g)}
                                        </span>
                                    </td>

                                    {/* WP Status */}
                                    <td className="px-4 py-3 text-[#64748b]">
                                        {g.wp?.status
                                            ? <span className="capitalize">{g.wp.status}</span>
                                            : <span className="italic text-[#cbd5e1]">—</span>}
                                    </td>

                                    {/* DB Status */}
                                    <td className="px-4 py-3 text-[#64748b]">
                                        {g.db?.status
                                            ? <span className="capitalize">{g.db.status}</span>
                                            : <span className="italic text-[#cbd5e1]">—</span>}
                                    </td>

                                    {/* Sync pill */}
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${cfg.pill}`}>
                                            {cfg.label}
                                        </span>
                                    </td>

                                    {/* Drifted fields */}
                                    <td className="px-4 py-3">
                                        {g.drifted_fields.length > 0 ? (
                                            <span className="text-[11px] text-amber-600 font-semibold">
                                                {g.drifted_fields.join(", ")}
                                            </span>
                                        ) : (
                                            <span className="text-[#cbd5e1]">—</span>
                                        )}
                                    </td>

                                    {/* Expand chevron */}
                                    <td className="px-2 py-3 text-right">
                                        <span className="material-symbols-outlined text-[#94a3b8] text-base">
                                            {isOpen ? "expand_less" : "expand_more"}
                                        </span>
                                    </td>
                                </tr>

                                {isOpen && <DetailDrawer group={g} />}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function WebsiteSyncPanel({ onBack }: { onBack?: () => void } = {}) {
    const [scanning, setScanning]       = useState(false);
    const [result, setResult]           = useState<ScanResult | null>(null);
    const [error, setError]             = useState<string | null>(null);

    useEffect(() => {
        fetchWithAuth("/api/v1/staff/website-scan/latest")
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setResult(data); })
            .catch(() => {});
    }, []);
    const [syncFilters, setSyncFilters] = useState<Set<SyncStatus>>(new Set());
    const [typeFilters, setTypeFilters] = useState<Set<string>>(new Set());
    const [search, setSearch]           = useState("");

    function toggleSync(v: SyncStatus) {
        setSyncFilters(prev => {
            const next = new Set(prev);
            next.has(v) ? next.delete(v) : next.add(v);
            return next;
        });
    }

    function toggleType(v: string) {
        setTypeFilters(prev => {
            const next = new Set(prev);
            next.has(v) ? next.delete(v) : next.add(v);
            return next;
        });
    }

    async function runScan() {
        setScanning(true);
        setError(null);
        try {
            const res = await fetchWithAuth("/api/v1/staff/website-scan", { method: "POST" });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            setResult(await res.json());
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Scan failed");
        } finally {
            setScanning(false);
        }
    }

    const filtered = result?.groups.filter(g => {
        if (syncFilters.size > 0 && !syncFilters.has(g.sync_status)) return false;
        if (typeFilters.size > 0 && !typeFilters.has(g.type)) return false;
        if (search && !g.name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    }) ?? [];

    const [exporting, setExporting] = useState(false);

    async function exportExcel() {
        setExporting(true);
        try {
            const res = await fetchWithAuth("/api/v1/staff/groups/export-excel");
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `groups_acf_${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert("Export failed: " + (e instanceof Error ? e.message : e));
        } finally {
            setExporting(false);
        }
    }

    const scannedAt = result ? new Date(result.scanned_at).toLocaleString() : null;

    return (
        <div className="flex flex-col gap-5 p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="mt-0.5 flex items-center gap-1 text-[#64748b] hover:text-[#003d45] transition-colors text-[13px] font-semibold"
                        >
                            <span className="material-symbols-outlined text-base">arrow_back</span>
                            Back
                        </button>
                    )}
                    <div>
                        <h2 className="text-xl font-black text-[#003d45]">Website Sync</h2>
                        <p className="text-[13px] text-[#64748b] mt-0.5">
                            Compare VolunteerHub groups with what&apos;s published on cmtassociation.org (via WordPress API)
                        </p>
                        {scannedAt && (
                            <p className="text-[11px] text-[#94a3b8] mt-1">Last scanned: {scannedAt}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={exportExcel}
                        disabled={exporting}
                        className="flex items-center gap-2 bg-[#166534] text-white px-4 py-2.5 rounded-xl text-[13px] font-bold hover:bg-[#14532d] transition-colors disabled:opacity-60"
                    >
                        <span className={`material-symbols-outlined text-base ${exporting ? "animate-spin" : ""}`}>
                            {exporting ? "sync" : "download"}
                        </span>
                        {exporting ? "Exporting…" : "Export Excel"}
                    </button>
                    <button
                        onClick={runScan}
                        disabled={scanning}
                        className="flex items-center gap-2 bg-[#003d45] text-white px-5 py-2.5 rounded-xl text-[13px] font-bold hover:bg-[#005060] transition-colors disabled:opacity-60"
                    >
                        <span className={`material-symbols-outlined text-base ${scanning ? "animate-spin" : ""}`}>
                            {scanning ? "sync" : "travel_explore"}
                        </span>
                        {scanning ? "Scanning…" : "Run Live Scan"}
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-[13px] text-rose-700">
                    <strong>Scan failed:</strong> {error}
                </div>
            )}

            {/* Loading */}
            {scanning && (
                <div className="flex flex-col items-center gap-3 py-16 text-[#64748b]">
                    <div className="w-10 h-10 border-2 border-[#003d45]/20 border-t-[#003d45] rounded-full animate-spin" />
                    <p className="text-[13px] font-semibold">Fetching WordPress groups and comparing…</p>
                    <p className="text-[11px] text-[#94a3b8]">This may take 5–10 seconds</p>
                </div>
            )}

            {/* Results */}
            {result && !scanning && (
                <>
                    {/* Summary strip */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                        {[
                            { label: "WordPress",   value: result.summary.total_wp, color: "text-[#003d45]" },
                            { label: "Our DB",       value: result.summary.total_db, color: "text-[#003d45]" },
                            { label: "Matched",      value: result.summary.matched,  color: "text-emerald-600" },
                            { label: "Field Drift",  value: result.summary.drift,    color: "text-amber-600" },
                            { label: "WP Only",      value: result.summary.only_wp,  color: "text-blue-600" },
                            { label: "DB Only",      value: result.summary.only_db,  color: "text-rose-600" },
                        ].map(c => (
                            <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-3">
                                <p className={`text-2xl font-black ${c.color}`}>{c.value}</p>
                                <p className="text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide mt-0.5">{c.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Alert banner */}
                    {result.summary.in_sync ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-emerald-700 text-[13px] font-semibold">
                            <span className="material-symbols-outlined text-base">check_circle</span>
                            All groups are in sync with WordPress.
                        </div>
                    ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-amber-700 text-[13px] font-semibold">
                            <span className="material-symbols-outlined text-base">warning</span>
                            {result.summary.drift + result.summary.only_wp + result.summary.only_db} group(s) need attention.
                        </div>
                    )}

                    {/* Filters + search */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-3 flex-wrap">
                            {/* Sync status filters */}
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#94a3b8] mr-1">Sync</span>
                                {SYNC_FILTER_OPTIONS.map(opt => {
                                    const active = syncFilters.has(opt.value);
                                    const cfg = STATUS_CONFIG[opt.value];
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => toggleSync(opt.value)}
                                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors
                                                ${active
                                                    ? "bg-[#003d45] text-white"
                                                    : "bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#003d45]"}`}
                                        >
                                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${active ? "bg-white/70" : cfg.dot}`} />
                                            {opt.label}
                                            <span className="ml-1 opacity-60">
                                                ({result.groups.filter(g => g.sync_status === opt.value).length})
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="w-px h-5 bg-[#e2e8f0]" />

                            {/* Group type filters */}
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#94a3b8] mr-1">Type</span>
                                {TYPE_FILTER_OPTIONS.map(opt => {
                                    const active = typeFilters.has(opt.value);
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => toggleType(opt.value)}
                                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors
                                                ${active
                                                    ? "bg-[#003d45] text-white"
                                                    : `bg-white border border-[#e2e8f0] hover:border-[#003d45] ${TYPE_STYLE[opt.value] || "text-[#64748b]"}`}`}
                                        >
                                            {opt.label}
                                            <span className="ml-1 opacity-60">
                                                ({result.groups.filter(g => g.type === opt.value).length})
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex-1" />

                            {/* Clear filters */}
                            {(syncFilters.size > 0 || typeFilters.size > 0) && (
                                <button
                                    onClick={() => { setSyncFilters(new Set()); setTypeFilters(new Set()); }}
                                    className="text-[11px] text-[#94a3b8] hover:text-[#003d45] font-semibold transition-colors"
                                >
                                    Clear filters
                                </button>
                            )}

                            <input
                                type="text"
                                placeholder="Search groups…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="border border-[#e2e8f0] rounded-lg px-3 py-1.5 text-[13px] text-[#334155] outline-none focus:border-[#003d45] w-52"
                            />
                        </div>

                        {/* Active filter summary */}
                        {(syncFilters.size > 0 || typeFilters.size > 0) && (
                            <p className="text-[11px] text-[#64748b]">
                                Showing {filtered.length} of {result.groups.length} groups
                                {syncFilters.size > 0 && <> · sync: {[...syncFilters].map(v => STATUS_CONFIG[v].label).join(", ")}</>}
                                {typeFilters.size > 0 && <> · type: {[...typeFilters].join(", ")}</>}
                            </p>
                        )}
                    </div>

                    {/* Table */}
                    <SyncTable groups={filtered} />
                </>
            )}

            {/* Empty state */}
            {!result && !scanning && !error && (
                <div className="flex flex-col items-center gap-3 py-20 text-[#94a3b8]">
                    <span className="material-symbols-outlined text-5xl">travel_explore</span>
                    <p className="text-[14px] font-semibold">Click &ldquo;Run Live Scan&rdquo; to compare groups</p>
                    <p className="text-[12px]">Pulls all groups from WordPress API and diffs against our database</p>
                </div>
            )}
        </div>
    );
}
